import { jsx as _jsx } from "react/jsx-runtime";
/**
 * The active scene's **Project settings**, from `res://project.godot`. A scene
 * without one renders at Godot's defaults, so the file loads through
 * `FileEventBus.tryLoad` (ADR-0028), never `useResource`, which reports a miss.
 * The default (no settings, theme scale 1.0) needs no provider.
 */
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { parseProjectSettings, projectThemeScale, projectViewportSize, projectMsaaModes, DEFAULT_THEME_SCALE, DEFAULT_VIEWPORT_WIDTH, DEFAULT_VIEWPORT_HEIGHT, } from '../../parser/projectSettingsParser.js';
import { PROJECT_FILE_PATH } from '../../godot/project/project.js';
import { useResourceLoader } from '../../resources/useResource.js';
import * as logger from '../../logger.js';
const DEFAULT_VALUE = {
    settings: null,
    themeScale: DEFAULT_THEME_SCALE,
    viewportSize: {
        width: DEFAULT_VIEWPORT_WIDTH,
        height: DEFAULT_VIEWPORT_HEIGHT,
    },
    msaaModes: projectMsaaModes(null),
};
const ProjectSettingsContext = createContext(DEFAULT_VALUE);
ProjectSettingsContext.displayName = 'ProjectSettingsContext';
/** The project's settings, or null when the file is absent or is not a `project.godot`. */
async function readProjectSettings(bus) {
    const content = await bus.tryLoad(PROJECT_FILE_PATH, 'ProjectSettings');
    if (typeof content !== 'string')
        return null;
    const parsed = parseProjectSettings(content);
    if (parsed) {
        logger.info(`[ProjectSettings] Loaded ${Object.keys(parsed).length} settings from ${PROJECT_FILE_PATH}`);
    }
    return parsed;
}
export function ProjectSettingsProvider({ children, sceneKey }) {
    const loader = useResourceLoader();
    const [settings, setSettings] = useState(null);
    useEffect(() => {
        const bus = loader?.fileEventBus;
        if (!bus) {
            setSettings(null);
            return undefined;
        }
        // Each read takes a number, and only the latest may land: a read that a change
        // overtook could otherwise resolve last and restore the old settings. The
        // cleanup takes a number too, so no read lands after it.
        let latestRead = 0;
        const read = async () => {
            const thisRead = ++latestRead;
            const parsed = await readProjectSettings(bus);
            if (thisRead === latestRead)
                setSettings(parsed);
        };
        // Cleared before the fetch, or one frame renders the scene at the previous
        // project's theme scale. A change to the file is not cleared first: the old
        // settings stay until the new ones arrive, so the theme scale never flashes.
        setSettings(null);
        void read();
        const onInvalidated = (path) => {
            if (path === PROJECT_FILE_PATH)
                void read();
        };
        bus.on('invalidated', onInvalidated);
        return () => {
            latestRead++;
            bus.off('invalidated', onInvalidated);
        };
    }, [loader, sceneKey]);
    const value = useMemo(() => ({
        settings,
        themeScale: projectThemeScale(settings),
        viewportSize: projectViewportSize(settings),
        msaaModes: projectMsaaModes(settings),
    }), [settings]);
    return _jsx(ProjectSettingsContext.Provider, { value: value, children: children });
}
/** The active scene's project settings. Safe without a provider. */
export function useProjectSettings() {
    return useContext(ProjectSettingsContext);
}
//# sourceMappingURL=ProjectSettingsContext.js.map